import React, { useCallback, useReducer, useState, useMemo, useEffect, useRef } from 'react';
import { BasicPageTemplate } from '@askporter/component-library/BasicPageTemplate';
import { ChipList } from '@askporter/component-library/ChipList';
import { Error } from '@askporter/component-library/Error';
import { LinkedListView } from '@askporter/component-library/LinkedListView';
import { Pagination } from '@askporter/component-library/Pagination';
import { parseURLQueryParams } from '@askporter/component-library/parseURLQueryParams';
import { URLQueryParams } from '@askporter/component-library/URLQueryParams';
import Box from '@mui/system/Box';
import { keepPreviousData, QueryFunction, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import mergeWith from 'lodash/mergeWith';
import { useLocation } from 'react-router';
import {
  ErrorModel,
  ExtendedMetadataDataType,
  SearchTaskFilter,
  SearchTaskRequest,
  SearchTaskResultExternal,
  SearchTaskSort,
  SearchTaskStats,
  TaskFilterValuesExternal,
  TaskTypeReadExternal,
} from '@askporter/client-grieg-lyric';
import { useToast } from '@askporter/component-library';
import { ExportModalWrapper } from '@askporter/exports';
import { queryKeys } from '@askporter/query-keys';
import { TaskListSideBar } from '@askporter/task-list-sidebar';
import {
  generateTaskSearchFilters,
  mergeArraysWithObjects,
  LinkedTaskMenuOptions,
  MutationResultItem,
  TaskFilterPermissions,
} from '@askporter/utils';
import { API } from '../../api';
import linkedTasksReducer, { initialState } from '../../reducer';
import { LinkedTasksState } from '../../types';
import {
  generateMenuOptions,
  transformTaskFilterValues,
  getActiveTaskFilterChips,
  useEntityFilterNames,
} from '../../utils';
import { taskUidFromURN, waitForLinkedState } from '../../utils/waitForLinkedState';
import { LinkedTasksSection } from '../LinkedTasksSection';
import { LinkTasks } from '../LinkTasks';
export interface LinkedTasksProps {
  t: (key: string, options?: Record<string, string | number>) => string;
  isSmallDevice: boolean;
  linkedListFilter: SearchTaskFilter;
  linkFormFilter: SearchTaskFilter;
  onLink: (tasks: Array<string>) => Promise<void>;
  onDelete: (taskURN: string) => Promise<MutationResultItem<AxiosResponse>>;
  permittedMenuOptions: LinkedTaskMenuOptions[];
  showAddButton?: boolean;
  onCountShouldChange: () => void | Promise<unknown>;
  filterPermissions: TaskFilterPermissions;
  postQueryFn: QueryFunction<SearchTaskResultExternal, [string, Record<string, unknown>]>;
  isExportEnabled?: boolean;
  /**
   * The count for number of linked tasks
   */
  linkedTaskCount: number;
  subtitle?: string;
}
/**
 * Renders the linked task form, a list of tasks based on the provided filter. This also includes the link task button
 * and form.
 *
 * @param t - The translation function
 * @param isSmallDevice - small viewport or not
 * @param linkedListFilter - filter for the list of linked tasks
 * @param linkFormFilter - filter passed through to the LinkTasks form
 * @param onLink - function to link tasks to an entity
 * @param onDelete - function to delete a relationship to a task
 * @param showAddButton - defaults to false, if true the add task button will be displayed
 * @param onCountChange - function that is executed after an event that results in the overall linked task count
 * changing
 * @param filterPermissions - object describing the filter visibility
 * @param TaskTypeGroup - component to render the task type group
 * @param postQueryFn - React query POST function for task searches
 *
 */
export const LinkedTasks: React.FC<React.PropsWithChildren<LinkedTasksProps>> = ({
  t,
  isSmallDevice,
  linkedListFilter,
  linkFormFilter,
  onLink,
  onDelete,
  permittedMenuOptions,
  showAddButton = false,
  onCountShouldChange,
  filterPermissions,
  postQueryFn,
  isExportEnabled,
  linkedTaskCount,
  subtitle = '',
}: LinkedTasksProps) => {
  const [linkFormIsVisible, setLinkFormIsVisible] = useState(false);
  const [hasClickedLinkButton, setHasClickedLinkButton] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  // number of skeleton rows to show while a link or removal waits for task search to catch up, or null when
  // nothing is in flight
  const [pendingSkeletonCount, setPendingSkeletonCount] = useState<number | null>(null);
  const [{ search, isSearchEnabled }, dispatch] = useReducer(
    linkedTasksReducer,
    initialState(linkedListFilter),
    (state): LinkedTasksState => {
      const { filter = {}, ...rest } = parseURLQueryParams<SearchTaskRequest>(Object.keys(state.search));
      state.search = {
        ...state.search,
        ...rest,
        filter: {
          ...state.search.filter,
          ...filter,
        },
      };
      return state;
    },
  );
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const unmountSignal = useRef<AbortSignal>();
  useEffect(() => {
    const controller = new AbortController();
    unmountSignal.current = controller.signal;
    return () => controller.abort();
  }, []);
  const detailsPageURL = location?.pathname?.substr(0, location?.pathname?.lastIndexOf('/')) || '/app';
  // memoize the filterBody which is provided to react query, only update when filters change
  const memoizedFilterBody = useMemo(() => generateTaskSearchFilters(search.filter), [search.filter]);
  // dispatch wrappers
  const setPage = (page: number) => dispatch({ type: 'SET_PAGE', payload: page });
  const setSearchFreeText = (text: string) => dispatch({ type: 'SET_SEARCH_FREE_TEXT', payload: text });
  const setFilters = (filters: SearchTaskFilter) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };
  const setSort = (sort: SearchTaskSort) => dispatch({ type: 'SET_SORT', payload: sort });
  const setIsSearchEnabled = (enabled: boolean) => dispatch({ type: 'SET_IS_SEARCH_ENABLED', payload: enabled });
  const clearAllFilters = (filter: SearchTaskRequest['filter']) =>
    dispatch({ type: 'CLEAR_ALL_FILTERS', payload: filter });
  const setExtendedMetadataStats = (extendedMetadataStats: Array<string>) =>
    dispatch({ type: 'SET_EXTENDED_METADATA_STATS', payload: extendedMetadataStats });
  const [stats, setStats] = useState<SearchTaskStats>();
  // this needs be passed as a filter to the react query below
  // so the total / global counts can be computed against the linked entity
  const linkedEntityFilterKey = Object.keys(linkedListFilter || {}).find((name) => /linked/.test(name));
  // GET /api/v1/task-filter-values
  const {
    data: taskFilterValuesData,
    isPending: taskFiltersLoading,
    isSuccess: taskFiltersSuccess,
    isError: taskFilterValuesIsError,
  } = useQuery<TaskFilterValuesExternal>({
    queryKey: queryKeys.tasks.filterValues.all,
    queryFn: () => API().get({ path: 'task-filter-values' }),
  });
  useEffect(() => {
    if (taskFilterValuesData) {
      const extendedMetadataStats = taskFilterValuesData?.extendedMetadata?.reduce((prev, curr) => {
        if (curr.dataType === ExtendedMetadataDataType.String) return [...prev, curr.qualifiedJsonElementName];
        return prev;
      }, []);
      setExtendedMetadataStats(extendedMetadataStats);
    }
  }, [taskFilterValuesData]);
  // POST /api/v1/tasks/search (for obtaining the 'global' stats)
  const {
    isSuccess: unfilteredLinkedTaskSearchSuccess,
    data: unfilteredLinkedTaskSearch,
    isError: unfilteredLinkedTaskSearchError,
    refetch: refetchStats,
  } = useQuery<SearchTaskResultExternal, AxiosError<ErrorModel>>({
    queryKey: queryKeys.tasks.search.stats({
      postData: {
        page: 1,
        filter: { [linkedEntityFilterKey]: linkedListFilter[linkedEntityFilterKey as keyof SearchTaskFilter] },
        extendedMetadataStats: search.extendedMetadataStats || [],
      },
      isForFetchingAllStats: true,
    }),
    queryFn: postQueryFn,
    gcTime: 500,
    enabled: !!taskFilterValuesData,
  });
  // this query represents what a user see's on the linked tasks page, it is subject to user driven filtering
  const linkedListQuery = (page: number) => ({
    queryKey: queryKeys.tasks.search.linkedList({ ...search, page, filter: memoizedFilterBody }),
    queryFn: async () => {
      const { data } = await API().post({
        path: 'tasks/search',
        payload: { ...search, page, filter: memoizedFilterBody },
        additionalHeaders: { 'x-usecase': 'filtered_search' },
      });
      return data as SearchTaskResultExternal;
    },
  });
  const {
    data: filteredLinkedTasks,
    isSuccess: filteredLinkedTasksSuccess,
    refetch: refetchCount,
    isError: isFilteredLinkedTasksError,
    error: filteredLinkedTasksError,
  } = useQuery<SearchTaskResultExternal, AxiosError<ErrorModel>>({
    ...linkedListQuery(search.page),
    enabled: !!taskFilterValuesData && isSearchEnabled,
    placeholderData: keepPreviousData,
  });
  // merge the stats from the unfiltered request with the filtered one
  useEffect(() => {
    if (
      filteredLinkedTasksSuccess &&
      unfilteredLinkedTaskSearchSuccess &&
      !unfilteredLinkedTaskSearchError &&
      unfilteredLinkedTaskSearch?.stats &&
      filteredLinkedTasks?.stats
    ) {
      setStats(
        mergeWith(
          {},
          unfilteredLinkedTaskSearch.stats,
          filteredLinkedTasks.stats,
          mergeArraysWithObjects,
        ) as SearchTaskStats,
      );
    } else if (
      // fallback to 'stats' from the filtered search results
      !unfilteredLinkedTaskSearchSuccess &&
      unfilteredLinkedTaskSearchError &&
      filteredLinkedTasks?.stats &&
      filteredLinkedTasksSuccess
    ) {
      setStats(mergeWith({}, filteredLinkedTasks.stats) as SearchTaskStats);
    }
  }, [
    filteredLinkedTasksSuccess,
    unfilteredLinkedTaskSearchSuccess,
    unfilteredLinkedTaskSearchError,
    unfilteredLinkedTaskSearch,
    filteredLinkedTasks,
  ]);
  // the list reads from task search, which only reflects a relationship write a few seconds later, so wait
  // for search to confirm the change before refetching
  const scopeFilter: SearchTaskFilter = linkedEntityFilterKey
    ? { [linkedEntityFilterKey]: linkedListFilter[linkedEntityFilterKey as keyof SearchTaskFilter] }
    : {};
  const refetchAfterChange = async (taskURNs: Array<string>, expect: 'present' | 'absent'): Promise<void> => {
    const signal = unmountSignal.current;
    await waitForLinkedState({ scopeFilter, taskUids: taskURNs.map(taskUidFromURN), expect, signal });
    if (signal?.aborted) return;
    const [{ data }] = await Promise.all([refetchCount(), refetchStats(), onCountShouldChange()]);
    // removing the only task on the last page would otherwise leave an empty page with no pagination to leave it
    const totalPages = data?.summary?.totalPages ?? 0;
    if (data?.results?.length === 0 && search.page > 1 && totalPages > 0) {
      // core-ui's default gcTime of 0 would evict this before the list reads it
      await queryClient.fetchQuery({ ...linkedListQuery(totalPages), gcTime: 5000 });
      setPage(totalPages);
    }
  };
  const hasLinkedTasks = linkedTaskCount > 0;
  const visibleSkeletonCount = Math.min(Math.max(filteredLinkedTasks?.results?.length ?? linkedTaskCount ?? 0, 1), 10);
  // handlers
  const handleDeleteLinkedTask = async (taskURN: string): Promise<void> => {
    setPendingSkeletonCount(visibleSkeletonCount);
    try {
      const result = await onDelete(taskURN);
      if (result && typeof result === 'object' && 'error' in result) {
        showError(t('ns.common:error:problem'));
        return;
      }
      await refetchAfterChange([taskURN], 'absent');
    } finally {
      setPendingSkeletonCount(null);
    }
  };
  const handleTasksOnLink = async (tasks: Array<string>): Promise<void> => {
    setPendingSkeletonCount(hasLinkedTasks ? visibleSkeletonCount : Math.min(tasks.length, 10));
    try {
      await onLink(tasks);
      await refetchAfterChange(tasks, 'present');
    } catch {
      showError(t('ns.common:error:problem'));
    } finally {
      setPendingSkeletonCount(null);
    }
  };
  const handlePaginationOnChange = useCallback(
    (_: any, page: number) => {
      setPage(page);
      window.scrollTo(0, 0);
    },
    [dispatch],
  );
  const handleSearchSubmit = (searchBoxValue: string) => {
    if (searchBoxValue !== search?.freeText) {
      setSearchFreeText(searchBoxValue);
      setIsSearchEnabled(true);
    }
  };
  // drives the sidebar's own checkboxes - counts decide what's offered, unlike the active-filter chips below
  const taskFilterValues = {
    priority: transformTaskFilterValues({
      values: taskFilterValuesData?.priorities,
      stats: stats?.priorities,
      groupByDisplayName: true,
    }),
    status: transformTaskFilterValues({
      values: taskFilterValuesData?.statuses,
      stats: stats?.statuses,
      groupByDisplayName: true,
    }),
  };
  // linkedEntityFilterKey holds this page's own scope (e.g. "only tasks linked to this asset") - it's baked
  // into linkedListFilter, not something the sidebar ever offers a control for (that's exactly why it's
  // excluded from filterPermissions for this context), so it must never surface as a removable chip either -
  // doing so would let a user query across every entity of that type, not just the one they're viewing
  const linkedEntityFilterValue =
    (linkedEntityFilterKey
      ? (linkedListFilter?.[linkedEntityFilterKey as keyof SearchTaskFilter] as string[] | undefined)
      : undefined) ?? [];
  const chipEligibleFilters: SearchTaskFilter = { ...search.filter };
  if (linkedEntityFilterKey) {
    const key = linkedEntityFilterKey as keyof SearchTaskFilter;
    const remaining = ((chipEligibleFilters[key] as string[] | undefined) ?? []).filter(
      (uid) => !linkedEntityFilterValue.includes(uid),
    );
    chipEligibleFilters[key] = (remaining.length ? remaining : undefined) as never;
  }
  // resolves display names for the entity-based filters (linked/assigned/escalated-to user, assigned/
  // escalated-to team, linked asset) as soon as they're selected in the drawer, ahead of being applied -
  // pre-warms the cache so the chip doesn't have to show a skeleton once it does appear
  const entityFilterNames = useEntityFilterNames(chipEligibleFilters);
  // the chip list should only reflect *applied* filters, not every checkbox click while the drawer is still
  // open - the sidebar already commits each click straight to search.filter (that's what drives the list
  // itself, unchanged here), so this snapshot is what decouples the chips from that live state. It's
  // refreshed when the drawer closes, whether via "Apply" or the cancel/X button - by then search.filter
  // holds the right value either way (applied, or reverted back to what it was before opening) - and updated
  // directly when a chip's own remove button is clicked, since that's an explicit action of its own.
  const [chipFilterSnapshot, setChipFilterSnapshot] = useState<SearchTaskFilter>(chipEligibleFilters);
  useEffect(() => {
    if (!showSidebar) setChipFilterSnapshot(chipEligibleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar]);
  // chip labels come from the raw (not count-filtered) catalogs above - unlike the sidebar's checkboxes, a
  // chip must keep representing an applied filter even once it no longer matches anything (e.g. after a
  // refresh), otherwise the list can show "no results" with no visible indication of the filter causing it
  const activeFilterChips = getActiveTaskFilterChips(
    chipFilterSnapshot,
    {
      priorities: taskFilterValuesData?.priorities,
      statuses: taskFilterValuesData?.statuses,
      taskTypes: taskFilterValuesData?.taskTypes,
    },
    taskFiltersLoading,
    entityFilterNames,
    t,
  );
  const handleRemoveFilterChip = (chipId: string) => {
    const chip = activeFilterChips.find((activeChip) => activeChip.id === chipId);
    if (!chip) return;
    const patch = { ...chip.removePatch };
    if (linkedEntityFilterKey && linkedEntityFilterKey in patch) {
      const key = linkedEntityFilterKey as keyof SearchTaskFilter;
      patch[key] = [...linkedEntityFilterValue, ...((patch[key] as string[] | undefined) ?? [])] as never;
    }
    setFilters(patch);
    setChipFilterSnapshot((current) => ({ ...current, ...chip.removePatch }));
  };
  const isPendingChange = pendingSkeletonCount !== null;
  // on first mount (e.g. a page refresh) there's no `placeholderData` to fall back on, so results are
  // `undefined` until the very first fetch settles - show skeleton rows through that window too, otherwise the
  // list looks empty for however long the initial search takes
  const isInitialResultsLoad = filteredLinkedTasks === undefined && !isFilteredLinkedTasksError;
  const skeletonCount = isPendingChange
    ? pendingSkeletonCount
    : isInitialResultsLoad
    ? Math.min(hasLinkedTasks ? linkedTaskCount : 3, 10)
    : 0;
  // used to generate the menu options for each task that is listed
  const menuOptions = (taskType: TaskTypeReadExternal, taskUid: string, taskURN: string) =>
    generateMenuOptions({
      permittedMenuOptions,
      isSmallDevice,
      iconPaths: { VIEW_TASK: taskType?.icon?.iconPath },
      handlers: {
        DELETE: () => handleDeleteLinkedTask(taskURN),
        VIEW_TASK: () => undefined,
      },
      navigationPaths: { VIEW_TASK: `/app/tasks/${taskUid}` },
      displayNames: {
        DELETE: t('ns.linked_tasks:task_options:remove'),
        VIEW_TASK: t('ns.linked_tasks:task_options:view_task'),
      },
    });
  if (isFilteredLinkedTasksError) {
    return (
      <BasicPageTemplate t={t} isSmallDevice={isSmallDevice} testId="linked-tasks-error">
        <Error
          t={t}
          statusCode={filteredLinkedTasksError?.response?.status}
          errLinkText={t('ns.task:extension:return_link')}
          homeLink={detailsPageURL}
        />
      </BasicPageTemplate>
    );
  }
  return (
    <LinkedListView
      t={t}
      pageTitle={t('ns.linked_tasks:page_title')}
      subtitle={subtitle}
      isSmallDevice={isSmallDevice}
      titleCount={linkedTaskCount}
      addButton={
        showAddButton
          ? {
              text: t('linked_tasks:link_task_button'),
              onClick: () => {
                // the first time the button is clicked we set has clicked to true, this mounts the LinkForm component
                if (!hasClickedLinkButton) setHasClickedLinkButton(true);
                setLinkFormIsVisible(true);
              },
              loading: isPendingChange,
            }
          : undefined
      }
      exportButton={
        isExportEnabled && {
          label: t('linked_tasks:export_button:label'),
          isSmallDevice,
          render: ({ handleClose }) => (
            <ExportModalWrapper
              t={t}
              isSmallDevice={isSmallDevice}
              totalSearchResults={linkedTaskCount}
              resourceName="tasks"
              filter={memoizedFilterBody}
              freeText={search.freeText}
              handleClose={handleClose}
            />
          ),
          disabled: linkedTaskCount === 0,
        }
      }
      searchBox={{
        label: t('ns.common:search:tasks'),
        onSubmit: handleSearchSubmit,
        startValue: search?.freeText,
        searchButtonAria: t('ns.task_list:search_button_aria_label'),
        autocompleteEnabled: true,
        API: API,
        resourceName: 'tasks',
        size: 'small',
      }}
      filterButton={{
        text:
          activeFilterChips.length > 0
            ? `${t('ns.linked_tasks:filter_button')} (${activeFilterChips.length})`
            : t('ns.linked_tasks:filter_button'),
        onClick: () => setShowSidebar(true),
      }}
      quickFiltersComponent={
        activeFilterChips.length > 0 ? (
          <Box mt={3}>
            <ChipList chips={activeFilterChips} onRemove={handleRemoveFilterChip} color="blue" maxVisible={3} />
          </Box>
        ) : undefined
      }
      noResults={{
        isVisible: filteredLinkedTasks?.results?.length === 0 && skeletonCount === 0,
        message: t('ns.linked_tasks:no_linked_tasks'),
      }}
      sections={[
        {
          key: 'linked_tasks_section',
          children: (
            <LinkedTasksSection
              tasks={isPendingChange ? [] : filteredLinkedTasks?.results}
              generateMenuOptions={menuOptions}
              menuTitle={t('ns.linked_tasks:task_options:menu_title')}
              isSmallDevice={isSmallDevice}
              skeletonCount={skeletonCount}
            />
          ),
        },
      ]}
    >
      <>
        <URLQueryParams state={{ ...search, filter: memoizedFilterBody }} />
        {/* If showAddButton is true and the user has clicked the new task button for the firs time the Link Tasks
        form will be rendered, the visibility is controlled by state  */}
        {showAddButton && hasClickedLinkButton && (
          <LinkTasks
            linkedTasksUids={filteredLinkedTasks?.results?.map((result) => result?.uid) ?? []}
            isVisible={linkFormIsVisible}
            setVisibility={(visibility: boolean) => setLinkFormIsVisible(visibility)}
            onLink={handleTasksOnLink}
            t={t}
            isSmallDevice={isSmallDevice}
            filter={linkFormFilter}
          />
        )}
        <Box position="absolute" left={0}>
          <TaskListSideBar
            stats={stats}
            filters={search.filter}
            taskFilterValues={taskFilterValues}
            errors={{ taskFilterValuesIsError }}
            isLoading={taskFiltersLoading || !taskFiltersSuccess}
            t={t}
            isSmallDevice={isSmallDevice}
            actions={{
              clearAllFilters: () => clearAllFilters(linkedListFilter),
              setIsSearchEnabled,
              setFilters,
              setSort,
            }}
            filterPermissions={filterPermissions}
            sort={search.sort}
            sideBarDrawerMode={{
              show: showSidebar,
              onClose: () => setShowSidebar(false),
            }}
            extendedMetadataFilters={taskFilterValuesData?.extendedMetadata}
          />
        </Box>

        {!isPendingChange && filteredLinkedTasks?.results?.length > 0 ? (
          <Pagination
            page={search.page}
            count={filteredLinkedTasks?.summary?.totalPages || 0}
            limitCount={499}
            onChange={handlePaginationOnChange}
          />
        ) : null}
      </>
    </LinkedListView>
  );
};
